import React, { useState, useEffect } from "react";
import {
  Paper,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  LinearProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCancel,
  faCircleStop,
  faTrashAlt,
  faBoxArchive,
  faEllipsisV,
  faGear,
  faCheckCircle,
  faClock,
  faUser,
  faLongArrowAltRight,
  faPlayCircle,
  faRedo,
  faTree,
} from "@fortawesome/free-solid-svg-icons";
import { ref, update, set, remove } from "firebase/database";
import { db } from "../../firebase";
import Notification from "../Notification"; // Import Notification Component
import DeploymentLogsModal from "./DeploymentLogsModal";
import { generateScreenConnectLink } from "../../Utils/generateScreenConnectLink"; // Import the helper function

function DeploymentCard({
  deployment,
  isArchived,
  setDeployments,
  setProcessingRestore,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [displayLogs, setDisplayLogs] = useState(false);
  const [screenConnectUrl, setScreenConnectUrl] = useState(null); // Store ScreenConnect URL

  useEffect(() => {
    // Fetch the sessionId and generate the ScreenConnect link
    if (deployment.machineName) {
      // Generate the ScreenConnect link
      console.log("Machine Name found: ", deployment.machineName);
      const url = generateScreenConnectLink(
        "cmd-zenzerosolutions.screenconnect.com",
        deployment.machineName,
      ); // Replace with your domain
      setScreenConnectUrl(url);
    } else {
      console.log("Machine Name not found");
      setScreenConnectUrl(null);
    }
  }, [deployment.machineName]);

  function closeLogs() {
    setDisplayLogs(false);
  }

  const archiveDeployment = async (deploymentId) => {
    const result = await Notification.confirmArchiveDeployment(theme);
    if (result.isConfirmed) {
      const deploymentRef = ref(db, `Deployments/${deploymentId}`);
      const archiveRef = ref(db, `Archive/${deploymentId}`);
  
      set(archiveRef, deployment)
        .then(() => remove(deploymentRef))
        .then(() => {
          Notification.success(
            theme,
            "Archived!",
            "The deployment has been archived."
          );
          // Remove the archived deployment from the list
          setDeployments((prevDeployments) =>
            prevDeployments.filter((d) => d.id !== deploymentId)
          );
        })
        .catch((error) => {
          console.error("Error archiving the deployment:", error);
          Notification.error(
            theme,
            "Error!",
            "There was a problem archiving the deployment."
          );
        });
    }
  };
  
  const restoreDeployment = async (deploymentId) => {
    setProcessingRestore(true);
    const result = await Notification.confirmRestoreDeployment(theme);
    if (result.isConfirmed) {
      const archiveRef = ref(db, `Deployments/${deploymentId}`);
      const deploymentRef = ref(db, `Archive/${deploymentId}`);

      set(archiveRef, deployment)
        .then(() => remove(deploymentRef))
        .then(() => {
          Notification.success(
            theme,
            "Restored!",
            "The deployment has been restored.",
          );
          setProcessingRestore(false);
        })
        .catch((error) => {
          console.error("Error restoring the deployment:", error);
          Notification.error(
            theme,
            "Error!",
            "There was a problem restoring the deployment.",
          );
          setProcessingRestore(false);
        });
    } else {
      setProcessingRestore(false);
    }
  };

  const deleteDeployment = async (deploymentId) => {
    const result = await Notification.confirmDeleteDeployment(theme);
    if (result.isConfirmed) {
      const deploymentRef = ref(db, `Deployments/${deploymentId}`);
      remove(deploymentRef)
        .then(() => {
          Notification.success(
            theme,
            "Deleted!",
            "The deployment has been deleted."
          );
          // Remove the deleted deployment from the list
          setDeployments((prevDeployments) =>
            prevDeployments.filter((d) => d.id !== deploymentId)
          );
        })
        .catch((error) => {
          console.error("Error deleting the deployment:", error);
          Notification.error(
            theme,
            "Error!",
            "There was a problem deleting the deployment."
          );
        });
    }
  };
  

  const skipDeployment = async (deploymentId) => {
    const result = await Notification.confirmSkipDeployment(
      theme,
      deployment.installStep,
    );
    if (result.isConfirmed) {
      const deploymentRef = ref(db, `Deployments/${deploymentId}`);
      update(deploymentRef, { remoteAction: "skip" })
        .then(() => {
          Notification.success(
            theme,
            "Skipped!",
            "The deployment has been marked as skipped.",
          );
        })
        .catch((error) => {
          console.error("Skip failed: ", error);
          Notification.error(
            theme,
            "Error!",
            "There was a problem skipping the deployment.",
          );
        });
    }
  };

  const cancelDeployment = async (deploymentId) => {
    const result = await Notification.confirmCancelDeployment(theme);
    if (result.isConfirmed) {
      const deploymentRef = ref(db, `Deployments/${deploymentId}`);
      update(deploymentRef, {
        remoteAction: "cancel",
        installStep: "Deployment Cancelled",
        setupDetails: "PC deployment has been cancelled.",
      })
        .then(() => {
          Notification.success(
            theme,
            "Cancelled!",
            "The deployment has been cancelled.",
          );
        })
        .catch((error) => {
          console.error("Error cancelling the deployment:", error);
          Notification.error(
            theme,
            "Error!",
            "There was a problem cancelling the deployment.",
          );
        });
    }
  };

  const resumeDeployment = async (deploymentId) => {
    const result = await Notification.confirmResumeDeployment(theme);
    if (result.isConfirmed) {
      const deploymentRef = ref(db, `Deployments/${deploymentId}`);
      update(deploymentRef, {
        remoteAction: "",
        installStep: "No Longer Cancelled!!",
        setupDetails: "Please run Deployment Manually from Automate",
      })
        .then(() => {
          Notification.success(
            theme,
            "Great News!",
            "The deployment may now be started from CWA.",
          );
        })
        .catch((error) => {
          console.error("Error resuming the deployment:", error);
          Notification.error(
            theme,
            "Error!",
            "There was a problem resuming the deployment.",
          );
        });
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const showLogs = (machineId) => {
    console.log(machineId);
    setDisplayLogs(true);
  };

  const handleMenuItemClick = (action) => {
    switch (action) {
      case "screenconnect":
        window.open(screenConnectUrl, "_blank");
        break;
      case "delete":
        deleteDeployment(deployment.id);
        break;
      case "archive":
        archiveDeployment(deployment.id);
        break;
      case "skip":
        skipDeployment(deployment.id);
        break;
      case "cancel":
        cancelDeployment(deployment.id);
        break;
      case "resume":
        resumeDeployment(deployment.id);
        break;
      case "restore":
        restoreDeployment(deployment.id);
        break;
      case "logs":
        showLogs(deployment.machineName);
        break;
      default:
        break;
    }
    setAnchorEl(null);
  };

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <DeploymentLogsModal
        onClose={closeLogs}
        open={displayLogs}
        machineName={deployment.machineName}
        clientCode={deployment.companyCode}
      />
      <Paper elevation={5}>
        <Card
          style={{
            zIndex: 900,
            width: "375px",
            minHeight: "320px",
            maxHeight: "320px",
            background: theme.palette.primary.gradient,
            color: theme.palette.success.text,
          }}
        >
          <CardHeader
            titleTypographyProps={{ variant: "body1" }}
            title={
              <Tooltip title={deployment.companyName} arrow>
                <span>{`${deployment.companyCode} - ${deployment.machineName}`}</span>
              </Tooltip>
            }
            style={{ color: theme.palette.success.text }}
            action={
              <div style={{ display: "flex", alignItems: "center" }}>
                {!isArchived && deployment.percentComplete === 100 && (
                  <Tooltip title="Archive deployment" arrow>
                    <IconButton onClick={() => handleMenuItemClick("archive")}>
                      <FontAwesomeIcon
                        icon={faBoxArchive}
                        style={{ color: "orange" }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {isArchived && (
                  <>
                    <Tooltip title="Restore deployment" arrow>
                      <IconButton
                        onClick={() => handleMenuItemClick("restore")}
                      >
                        <FontAwesomeIcon
                          icon={faRedo}
                          style={{ color: "orange" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                {deployment.remoteAction === "cancel" && (
                  <Tooltip title="Resume Deployment" arrow>
                    <IconButton onClick={() => handleMenuItemClick("resume")}>
                      <FontAwesomeIcon
                        icon={faPlayCircle}
                        style={{ color: "green" }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Menu" arrow>
                  <IconButton onClick={handleMenuOpen}>
                    <FontAwesomeIcon color="whitesmoke" icon={faEllipsisV} />
                  </IconButton>
                </Tooltip>
              </div>
            }
          />

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 4,
              style: {
                color: "whitesmoke",
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem
              title="View PC in ScreenConnect"
              onClick={() => handleMenuItemClick("screenconnect")}
              disabled={!screenConnectUrl}
            >
              <ListItemIcon>
                <FontAwesomeIcon
                  icon={faGear}
                  style={{ color: screenConnectUrl ? "limegreen" : "gray" }}
                />
              </ListItemIcon>
              <Typography variant="inherit" noWrap>
                ScreenConnect
              </Typography>
            </MenuItem>
            <MenuItem
              title="Delete Deployment from Database"
              onClick={() => handleMenuItemClick("delete")}
            >
              <ListItemIcon>
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  style={{ color: "magenta" }}
                />
              </ListItemIcon>
              <Typography variant="inherit" noWrap>
                Delete
              </Typography>
            </MenuItem>
            {!isArchived && (
              <>
                <MenuItem
                  title="Move Deployment to Archive DB"
                  onClick={() => handleMenuItemClick("archive")}
                >
                  <ListItemIcon>
                    <FontAwesomeIcon
                      icon={faBoxArchive}
                      style={{ color: "orange" }}
                    />
                  </ListItemIcon>
                  <Typography variant="inherit" noWrap>
                    Archive
                  </Typography>
                </MenuItem>
                <MenuItem
                  title="View Deployment Logs"
                  onClick={() => handleMenuItemClick("logs")}
                >
                  <ListItemIcon>
                    <FontAwesomeIcon icon={faTree} style={{ color: "brown" }} />
                  </ListItemIcon>
                  <Typography variant="inherit" noWrap>
                    Logs
                  </Typography>
                </MenuItem>
              </>
            )}
            {isArchived && (
              <MenuItem
                title="Restore Deployment from Archive DB"
                onClick={() => handleMenuItemClick("restore")}
              >
                <ListItemIcon>
                  <FontAwesomeIcon icon={faRedo} style={{ color: "orange" }} />
                </ListItemIcon>
                <Typography variant="inherit" noWrap>
                  Restore
                </Typography>
              </MenuItem>
            )}
            {!isArchived && deployment.percentComplete < 100 && (
              <>
                <Tooltip
                  title="Attempt to skip the current deployment step"
                  arrow
                >
                  <MenuItem onClick={() => handleMenuItemClick("skip")}>
                    <ListItemIcon>
                      <FontAwesomeIcon
                        icon={faLongArrowAltRight}
                        style={{ color: "blue" }}
                      />
                    </ListItemIcon>
                    <Typography variant="inherit" noWrap>
                      Skip Step
                    </Typography>
                  </MenuItem>
                </Tooltip>
                {deployment.remoteAction === "cancel" ? (
                  <MenuItem
                    title="Resume deployment"
                    onClick={() => handleMenuItemClick("resume")}
                  >
                    <ListItemIcon>
                      <FontAwesomeIcon
                        icon={faPlayCircle}
                        style={{ color: "green" }}
                      />
                    </ListItemIcon>
                    <Typography variant="inherit" noWrap>
                      Resume
                    </Typography>
                  </MenuItem>
                ) : (
                  <MenuItem
                    title="Cancel deployment and prevent JimmyBot from running on device"
                    onClick={() => handleMenuItemClick("cancel")}
                  >
                    <ListItemIcon>
                      <FontAwesomeIcon
                        scale={2}
                        icon={faCircleStop}
                        style={{ color: "red" }}
                      />
                    </ListItemIcon>
                    <Typography variant="inherit" noWrap>
                      Cancel
                    </Typography>
                  </MenuItem>
                )}
              </>
            )}
          </Menu>

          <CardContent
            style={{
              color: theme.palette.success.text,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {deployment.remoteAction === "cancel" ? (
              <div>
                <FontAwesomeIcon
                  icon={faCancel}
                  size="4x"
                  style={{ color: "red" }}
                />
              </div>
            ) : deployment.percentComplete < 100 ? (
              <div>
                <img
                  color={theme.palette.success.text}
                  alt="Lifebuoy zenzero logo"
                  className="App-logo"
                  src="/ZenzeroLifebuoyWhite.png"
                  width="65px"
                ></img>
              </div>
            ) : (
              <div>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  size="4x"
                  style={{ color: theme.palette.success.text }}
                />
              </div>
            )}

            <Typography
              textAlign={"center"}
              marginTop={"8px"}
              variant="h6"
              component="p"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={deployment.installStep}
            >
              {deployment.installStep ? (
                deployment.installStep
              ) : (
                <span style={{ color: theme.palette.success.text }}>
                  No Installation Details Found
                </span>
              )}
            </Typography>

            <Typography
              textAlign={"center"}
              variant="body2"
              component="p"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={deployment.setupDetails}
            >
              {deployment.setupDetails ? (
                deployment.setupDetails
              ) : (
                <span style={{ color: "gray" }}>
                  No setup details available
                </span>
              )}
            </Typography>
            { deployment.downloadProgress && deployment.downloadProgress > 1 && (
            <Tooltip title="Download Status" arrow>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "15px",
                  marginTop: "5px",
                  marginBottom: "0px",
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={deployment.downloadProgress}
                  style={{ height: "15px", width: "100%" }}
                />

                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{ color: theme.palette.success.text }}
                  >
                    {deployment.downloadProgress}%
                  </Typography>
                </div>
              </div>
            </Tooltip>
            )}

            <Tooltip title="Percent complete" arrow>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "20px",
                  marginTop: deployment.downloadProgress > 1 ? "30px" : "50px",
                  marginBottom: "0px",
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={deployment.percentComplete}
                  style={{ height: "20px", width: "100%" }}
                />

                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{ color: theme.palette.success.text }}
                  >
                    {deployment.percentComplete}%
                  </Typography>
                </div>
              </div>
            </Tooltip>
          </CardContent>

          <CardActions
            style={{ justifyContent: "space-between", marginTop: "-10px" }}
          >
            <Tooltip title="Logged in user" arrow>
              <div style={{ display: "flex", alignItems: "center" }}>
                <FontAwesomeIcon icon={faUser} style={{ marginRight: 5 }} />
                <Typography
                  color={theme.palette.success.text}
                  variant="body2"
                  component="p"
                >
                  {deployment.userName}
                </Typography>
              </div>
            </Tooltip>
            <Tooltip title="Deployment start time" arrow>
              <div style={{ display: "flex", alignItems: "center" }}>
                <FontAwesomeIcon icon={faClock} style={{ marginRight: 5 }} />
                <Typography
                  color={theme.palette.success.text}
                  variant="body2"
                  component="p"
                >
                  {new Date(deployment.startTime).toLocaleString()}
                </Typography>
              </div>
            </Tooltip>
          </CardActions>
        </Card>
      </Paper>
    </div>
  );
}

export default DeploymentCard;
